import { useTranslator } from '@translator-app/react';
import classNames from 'classnames';
import DayJS from 'dayjs';
import jsCookie from 'js-cookie';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React from 'react';

import { Button } from '../../../../components/atoms/button';
import { ErrorText } from '../../../../components/atoms/error-text';
import { Heading } from '../../../../components/atoms/heading';
import { Image } from '../../../../components/atoms/image';
import { Link } from '../../../../components/atoms/link';
import { TextField } from '../../../../components/atoms/textfield';
import { ErrorFallback } from '../../../../components/organisms/error-boundary';
import { NotFound } from '../../../../components/organisms/not-found';
import { OrderButtons } from '../../../../components/organisms/order-buttons';
import { OrderHint } from '../../../../components/organisms/order-hint';
import { OrderLayout } from '../../../../components/organisms/order-layout';
import { FallbackPageTemplate } from '../../../../components/templates/fallback-page-template';
import { ShopPageTemplate } from '../../../../components/templates/shop-page-template';
import { APPLICATION_VERSION, IS_PRODUCTION } from '../../../../constants';
import { ShoppingCart, useApplicationState } from '../../../../contexts/application-state-context';
import { useKioskMode } from '../../../../contexts/kiosk-context';
import { useLanguageContext } from '../../../../contexts/language-context';
import {
  AllergensEnumType,
  LanguageEnum,
  OrderObjectType,
  OrganisationObjectType,
  ReservationObjectType,
  ShopObjectType,
} from '../../../../generated/graphql';
import XIcon from '../../../../icons/x.svg';
import { createOrder, getOrganisationAndShop } from '../../../../utils/api';
import { getLastOrderCookieName } from '../../../../utils/api';
import { formatCurrency } from '../../../../utils/currency';
import { formatDateTimeString } from '../../../../utils/date-time';
import { isValidEmail } from '../../../../utils/email';
import { nullthrows } from '../../../../utils/invariant';
import { buildOrderPath } from '../../../../utils/order-path';
import {
  getReservationPrices,
  getReservationProductImages,
  getReservationProductsName,
} from '../../../../utils/product';
import { totalFromCart } from '../../../../utils/shopping-cart';
import { Sentry, captureException } from '../../../_app';

const LOCALSTORAGE_KEY = 'saved-contact-information';

function createKioskContact() {
  const randomId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : Math.random().toString(36).slice(2, 14);

  return {
    firstName: 'Kiosk',
    lastName: 'Kiosk',
    email: `kiosk+${randomId}@viresmo.com`,
  };
}

interface ShoppingCartItemProps {
  item: ReservationObjectType;
  organisation: OrganisationObjectType;
}

function ShoppingCartItemComponent(props: ShoppingCartItemProps) {
  let { item, organisation } = props;
  let router = useRouter();
  let { removeFromCart, shopId } = useApplicationState();
  let { translate } = useTranslator();

  let name = React.useMemo(() => {
    return getReservationProductsName(item);
  }, [item]);

  let images = React.useMemo(() => {
    return getReservationProductImages(item);
  }, [item]);

  const prices = React.useMemo(() => {
    return getReservationPrices(item);
  }, [item]);

  const allergens = React.useMemo(() => {
    const allergensSet = new Set<AllergensEnumType>();
    for (const product of item.vendingProducts) {
      if (product.product?.allergensCombined) {
        for (const allergen of product.product.allergensCombined) {
          if (allergen && allergen !== AllergensEnumType.None) allergensSet.add(allergen);
        }
      }
      for (const included of product.product?.includedProducts ?? []) {
        if (included.product?.allergensCombined) {
          for (const allergen of included.product.allergensCombined) {
            if (allergen && allergen !== AllergensEnumType.None) allergensSet.add(allergen);
          }
        }
      }
    }
    for (const product of item.freeProducts) {
      if (product.product?.allergensCombined) {
        for (const allergen of product.product.allergensCombined) {
          if (allergen && allergen !== AllergensEnumType.None) allergensSet.add(allergen);
        }
      }
    }
    return Array.from(allergensSet);
  }, [item]);

  let productLink = `/${router.locale}/${organisation.pathname}/${shopId}/product/${item._id}`;

  return (
    <div className="flex">
      {!!images.length && (
        <div className="w-1/4">
          <a href={productLink}>
            <Image url={images[0]} width={150} sizes="150px" alt={name} className="w-full h-auto" />
          </a>
        </div>
      )}
      <div
        className={classNames('pl-4', {
          'w-3/4': !!images.length,
          'w-full': !images.length,
        })}
      >
        <div className="flex justify-between">
          <div className="text-gray-700 text-lg font-display font-medium whitespace-pre-wrap break-words uppercase tracking-tight">
            <a href={productLink}>{name}</a>
          </div>
          <div>
            <Button
              isIcon
              appearance="subtle"
              onPress={() => {
                removeFromCart([item]);
              }}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {allergens.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {allergens
              .filter((a) => a !== AllergensEnumType.None)
              .map((allergen) => {
                const label = translate(`allergenEnum.${allergen}`);
                return (
                  <img
                    key={allergen}
                    src={`/allergen-icons/${allergen}.svg`}
                    alt={label}
                    title={label}
                    className="w-6 h-6 object-contain"
                  />
                );
              })}
          </div>
        )}
        <div className="flex justify-between items-center">
          <div className="flex items-baseline gap-2">
            {prices.discountAmount > 0 && (
              <span className="text-sm text-muted-foreground line-through">{formatCurrency(prices.totalPrice)}</span>
            )}
            <span
              className={classNames('font-bold', {
                'text-2xl text-red-600': prices.discountAmount > 0,
                'text-xl text-gray-700': prices.discountAmount === 0,
              })}
            >
              {formatCurrency(prices.totalPriceWithDiscount)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PageProps {
  organisation?: OrganisationObjectType;
  shop: ShopObjectType;
  reservations: Array<ReservationObjectType>;
  shoppingCart: ShoppingCart;
  order?: OrderObjectType;
  error?: { message: string };
}

function PageContent(props: PageProps) {
  let { shop } = props;
  let { shoppingCart, organisation, shopId } = useApplicationState();
  let router = useRouter();
  let { language } = useLanguageContext();
  let { translate } = useTranslator();
  let { isKioskMode } = useKioskMode();

  let [isLoading, setIsLoading] = React.useState(false);
  let [serverError, setServerError] = React.useState<null | Error>(null);

  let [formValues, setFormValues] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  let [errors, setErrors] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  let isEmpty = !shoppingCart.length;
  let pickupDateTime = React.useMemo(() => {
    return DayJS(Date.now()).add(shop.vendingPickupTime, 'hour');
  }, [shop]);

  const totalPrice = React.useMemo(() => {
    return totalFromCart(shoppingCart);
  }, [shoppingCart]);

  React.useEffect(() => {
    if (isKioskMode) {
      setFormValues(createKioskContact());
      return;
    }

    if (typeof window !== 'undefined') {
      try {
        const foundItem = nullthrows(localStorage.getItem(LOCALSTORAGE_KEY), 'No locally stored value');
        const savedContactInformation = JSON.parse(foundItem);
        if (savedContactInformation.version === APPLICATION_VERSION) {
          let { firstName, lastName, email } = savedContactInformation.values;
          setFormValues({ firstName, lastName, email });
        } else {
          localStorage.removeItem(LOCALSTORAGE_KEY);
        }
      } catch (err) {
        // do nothing...
      }
    }
  }, [isKioskMode]);

  const handleFormValueChange = (field: string) => {
    return (newValue: string) => {
      let valuesClone = { ...formValues };
      valuesClone[field] = newValue;
      localStorage.setItem(
        LOCALSTORAGE_KEY,
        JSON.stringify({
          version: APPLICATION_VERSION,
          values: valuesClone,
        })
      );
      setFormValues(valuesClone);
    };
  };

  const handlePlaceOrder = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setIsLoading(true);
    setServerError(null);

    try {
      const contactValues = isKioskMode ? createKioskContact() : formValues;
      let errors: any = {};

      if (!isKioskMode) {
        if (!contactValues.firstName) {
          errors.firstName = translate('shoppingCart.validation.firstNameRequired');
        }

        if (!contactValues.lastName) {
          errors.lastName = translate('shoppingCart.validation.lastNameRequired');
        }

        if (!isValidEmail(contactValues.email)) {
          errors.email = translate('shoppingCart.validation.emailInvalid');
        }
      }

      setErrors(errors);

      if (Object.keys(errors).length) {
        setIsLoading(false);
        return;
      }

      let orderData = await createOrder({
        shopId,
        language: LanguageEnum.Nl,
        firstName: contactValues.firstName,
        lastName: contactValues.lastName,
        email: contactValues.email,
        items: shoppingCart.map((i) => i._id),
        organisationId: shop.organisation?._id ?? organisation._id,
      });

      jsCookie.set(
        getLastOrderCookieName(organisation.pathname),
        {
          version: APPLICATION_VERSION,
          orderId: orderData._id,
          shortToken: orderData.shortToken,
        },
        {
          // Expire in 2 days
          expires: 2,
          secure: IS_PRODUCTION,
          sameSite: IS_PRODUCTION ? 'None' : undefined,
        }
      );

      window.location.href = buildOrderPath({
        locale: router.locale,
        shortToken: orderData.shortToken,
        pay: true,
      });
    } catch (err) {
      captureException(err);
      setServerError(err);
    }

    setIsLoading(false);
  };

  return (
    <div className="kiosk:min-h-screen kiosk:p-0 bg-gray-100 sm:py-8 min-h-screen-minus-nav kiosk:flex kiosk:items-center">
      <div className="bg-white p-8 max-w-lg mx-auto shadow-lg w-full">
        <Heading>{translate('shoppingCart.yourCart')}</Heading>
        {!isEmpty && (
          <div className="font-medium text-gray-600 mt-2">
            {translate('order.pickupAtDeadline', { pickupDateTime: formatDateTimeString(pickupDateTime, language) })}
          </div>
        )}
        <div className="mt-8 max-w-lg w-full">
          <OrderLayout totalPrice={totalPrice}>
            {!isEmpty ? (
              <div>
                {shoppingCart.map((item) => {
                  return <ShoppingCartItemComponent key={item._id} item={item} organisation={organisation} />;
                })}
              </div>
            ) : (
              <div className="font-medium text-center my-2">
                <div className="text-gray-600">{translate('shoppingCart.empty')}</div>
                <div className="py-2">
                  <Link href={`/${router.locale}/${organisation.pathname}/${shopId}`}>
                    {translate('products.viewOurAvailableProducts')}
                  </Link>
                </div>
              </div>
            )}
          </OrderLayout>
          {!isEmpty ? (
            <form className="mt-8" onSubmit={handlePlaceOrder} noValidate>
              {!isKioskMode && (
                <>
                  <div className="mb-4">
                    <TextField
                      label={translate('shoppingCart.firstName')}
                      type="text"
                      autoComplete="given-name"
                      value={formValues.firstName}
                      onChange={handleFormValueChange('firstName')}
                      placeholder="John"
                      error={errors.firstName}
                      disabled={isLoading}
                    ></TextField>
                  </div>
                  <div className="mb-4">
                    <TextField
                      label={translate('shoppingCart.lastName')}
                      type="text"
                      autoComplete="family-name"
                      value={formValues.lastName}
                      onChange={handleFormValueChange('lastName')}
                      placeholder="Doe"
                      error={errors.lastName}
                      disabled={isLoading}
                    ></TextField>
                  </div>
                  <div className="mb-4">
                    <TextField
                      label={translate('shoppingCart.email')}
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={formValues.email}
                      onChange={handleFormValueChange('email')}
                      placeholder="john@example.org"
                      error={errors.email}
                      disabled={isLoading}
                    ></TextField>
                  </div>
                </>
              )}
              <div className={isKioskMode ? undefined : 'mt-8'}>
                <ErrorText error={serverError} />
                <OrderButtons
                  prevButtonText={translate('general.previous')}
                  onPrevButtonPress={() => {
                    router.push(`/${organisation.pathname}/${shopId}`);
                  }}
                  nextButtonText={translate('general.next')}
                  nextButtonDisabled={!shoppingCart.length}
                  isLoading={isLoading}
                />
              </div>
            </form>
          ) : (
            <div className="mt-8">
              <OrderButtons
                prevButtonText={translate('general.previous')}
                onPrevButtonPress={() => {
                  router.push(`/${organisation.pathname}/${shopId}`);
                }}
                nextButtonText={translate('general.next')}
                nextButtonDisabled
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Index(props: PageProps) {
  let { organisation, shop, shoppingCart, order, reservations, error } = props;

  if (error) {
    return (
      <FallbackPageTemplate>
        <ErrorFallback error={error} />
      </FallbackPageTemplate>
    );
  }

  if (!organisation) {
    return (
      <FallbackPageTemplate>
        <NotFound />
      </FallbackPageTemplate>
    );
  }

  return (
    <ShopPageTemplate
      organisation={organisation}
      shop={shop}
      reservations={reservations}
      shoppingCart={shoppingCart}
      order={order}
    >
      <OrderHint />
      <PageContent {...props} />
    </ShopPageTemplate>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  try {
    let { shopId } = ctx.query;
    let organisationData = await getOrganisationAndShop(ctx, shopId as string);

    if (!organisationData) {
      return {
        props: {},
      };
    }

    return {
      props: {
        organisation: organisationData.organisation,
        shop: organisationData.shop,
        reservations: organisationData.reservations,
        shoppingCart: organisationData.shoppingCart,
        order: organisationData.order,
      },
    };
  } catch (err) {
    const eventId = captureException(err);
    await Sentry.flush();

    return {
      props: {
        error: {
          message: err.message,
          eventId,
        },
      },
    };
  }
}
